import api from './api';
import { API_URL } from '../config';

export interface Combo {
  id: number;
  name: string;
  description?: string;
  image_url?: string;
  active: boolean;
  created_at: string;
  updated_at: string;
  product_count?: number;
  products?: ComboProduct[];
}

export interface ComboProduct {
  id: number;
  combo_product_id?: number;
  name: string;
  description?: string;
  image_url?: string;
  brand?: string;
  model?: string;
  stock: number;
  category_name?: string;
  quantity: number;
  position: number;
}

export interface CreateComboData {
  name: string;
  description?: string;
  active?: boolean;
  products?: {
    product_id: number;
    quantity: number;
  }[];
}

export interface UpdateComboData extends CreateComboData {
  id: number;
}

export interface CombosResponse {
  combos: Combo[];
  currentPage: number;
  totalPages: number;
  total: number;
  hasNext: boolean;
  hasPrev: boolean;
}

class ComboService {
  async getCombos(
    page: number = 1,
    limit: number = 10,
    search: string = '',
    active?: boolean
  ): Promise<CombosResponse> {
    try {
      const params = new URLSearchParams();
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      if (search) {
        params.append('search', search);
      }
      
      if (active !== undefined) {
        params.append('active', active.toString());
      }

      const response = await api.get(`/combos?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener combos:', error);
      throw error;
    }
  }

  async getComboById(id: number): Promise<Combo> {
    try {
      const response = await api.get(`/combos/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener combo:', error);
      throw error;
    }
  }

  async createCombo(comboData: CreateComboData, imageFile?: File): Promise<Combo> {
    try {
      const formData = new FormData();
      
      formData.append('name', comboData.name);
      if (comboData.description) {
        formData.append('description', comboData.description);
      }
      formData.append('active', (comboData.active ?? true).toString());
      
      if (comboData.products) {
        formData.append('products', JSON.stringify(comboData.products));
      }
      
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await api.post('/combos', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error al crear combo:', error);
      throw error;
    }
  }

  async updateCombo(id: number, comboData: Partial<CreateComboData>, imageFile?: File): Promise<Combo> {
    try {
      const formData = new FormData();
      
      if (comboData.name) {
        formData.append('name', comboData.name);
      }
      if (comboData.description !== undefined) {
        formData.append('description', comboData.description);
      }
      if (comboData.active !== undefined) {
        formData.append('active', comboData.active.toString());
      }
      if (comboData.products !== undefined) {
        formData.append('products', JSON.stringify(comboData.products));
      }
      
      if (imageFile) {
        formData.append('image', imageFile);
      }

      const response = await api.put(`/combos/${id}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      console.error('Error al actualizar combo:', error);
      throw error;
    }
  }

  async deleteCombo(id: number): Promise<void> {
    try {
      await api.delete(`/combos/${id}`);
    } catch (error) {
      console.error('Error al eliminar combo:', error);
      throw error;
    }
  }

  async toggleComboStatus(id: number, active: boolean): Promise<Combo> {
    try {
      const response = await api.patch(`/combos/${id}`, { active });
      return response.data;
    } catch (error) {
      console.error('Error al cambiar estado del combo:', error);
      throw error;
    }
  }

  async getCombosByProduct(productId: number): Promise<Combo[]> {
    try {
      const response = await api.get(`/combos/product/${productId}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener combos por producto:', error);
      throw error;
    }
  }
}

export const comboService = new ComboService();